import {
  ContextObjectTypes,
  OrganizationRuleGroupCombinator,
  type PageConfigurationCondition,
  type PageConfigurationRuleGroup,
} from '@generated/graphql';
import { ObjectType, type RuleGroupDocument } from '@onevesthq/ov-conditions';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { RuleBuilder } from '@ovComponents/3-pattern/ruleBuilder/ruleBuilder';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { validateRule } from '@utils/common';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const contextObjectTypeToObjectType: Record<ContextObjectTypes, ObjectType> = {
  [ContextObjectTypes.Account]: ObjectType.ACCOUNT,
  [ContextObjectTypes.User]: ObjectType.USER,
};

export const ConditionalRuleBuilder = ({
  ruleGroup,
  setCondition,
  onValidationChange,
  showValidationError = true,
  contextObject = ContextObjectTypes.User,
  setContextObject,
}: {
  ruleGroup: RuleGroupDocument | undefined | null;
  setCondition: (c: PageConfigurationCondition | undefined) => void;
  onValidationChange?: (isValid: boolean) => void;
  showValidationError?: boolean;
  contextObject?: ContextObjectTypes;
  setContextObject?: (contextObject: ContextObjectTypes) => void;
}) => {
  const { t } = useTranslation([
    'workflowCompletions',
    'shared',
    'pageConfiguration',
  ]);
  const { sys } = useThemeTokens();

  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    if (ruleGroup) {
      const valid = validateRule(ruleGroup as PageConfigurationRuleGroup);
      setIsValid(valid);
      onValidationChange?.(valid);
    } else {
      setIsValid(true);
      onValidationChange?.(true);
    }
  }, [ruleGroup, onValidationChange]);

  return (
    <Box sx={{ pt: '32px' }}>
      {ruleGroup ? (
        <>
          <SelectField
            label={t('conditionOn')}
            fullWidth
            sx={{ mb: 3 }}
            value={contextObject}
            onChange={(e: any) =>
              setContextObject?.(e.target.value as ContextObjectTypes)
            }
          >
            <MenuItem value={ContextObjectTypes.User}>{t('client')}</MenuItem>
            <MenuItem value={ContextObjectTypes.Account}>
              {t('contextAccount')}
            </MenuItem>
          </SelectField>
          <RuleBuilder
            ruleGroup={ruleGroup}
            objectType={contextObjectTypeToObjectType[contextObject]}
            setRuleGroup={(r) => {
              setCondition({ ruleGroup: r as PageConfigurationRuleGroup });
            }}
          />
          <Button
            label={t('removeCondition')}
            sx={{ mt: 2 }}
            variant='tonal'
            size='sm'
            color='destructive'
            onClick={() => {
              setCondition(undefined);
            }}
          />
          {showValidationError && !isValid && (
            <Typography
              variant='bodyMedium'
              sx={{ mt: 2, color: sys.color.negative }}
            >
              {t('pageConfiguration:ruleGroupError')}
            </Typography>
          )}
        </>
      ) : (
        <Button
          label={t('addCondition')}
          onClick={() => {
            setCondition({
              ruleGroup: {
                combinator: OrganizationRuleGroupCombinator.Or,
                rules: [],
              },
            });
          }}
        />
      )}
    </Box>
  );
};
